import React, { useState } from 'react';
import { textPositionOptions, fontStyleOptions, offerTemplates } from '../data/vehicleData.js';
import SafeIcon from '../common/SafeIcon.jsx';
import * as FiIcons from 'react-icons/fi';

const { FiType, FiDollarSign, FiPlus, FiTrash2, FiEye, FiEyeOff } = FiIcons;

const TextOverlayEditor = ({ textOverlays, onTextOverlaysChange }) => {
  const [activeOverlay, setActiveOverlay] = useState(null);

  const addTextOverlay = () => {
    const newOverlay = {
      id: Date.now(),
      text: '',
      position: 'bottom-right',
      fontStyle: 'bold',
      color: '#FFFFFF',
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      fontSize: 'large',
      visible: true,
      type: 'custom'
    };
    onTextOverlaysChange([...textOverlays, newOverlay]);
    setActiveOverlay(newOverlay.id);
  };

  const addOfferOverlay = (template) => {
    const newOverlay = {
      id: Date.now(),
      text: template.template,
      position: 'top-right',
      fontStyle: 'bold',
      color: '#FFFFFF',
      backgroundColor: 'rgba(0, 123, 255, 0.9)',
      fontSize: 'large',
      visible: true,
      type: 'offer',
      template: template,
      fields: {}
    };
    onTextOverlaysChange([...textOverlays, newOverlay]);
    setActiveOverlay(newOverlay.id);
  };

  const updateOverlay = (id, updates) => {
    const updatedOverlays = textOverlays.map(overlay =>
      overlay.id === id ? { ...overlay, ...updates } : overlay
    );
    onTextOverlaysChange(updatedOverlays);
  };

  const removeOverlay = (id) => {
    const updatedOverlays = textOverlays.filter(overlay => overlay.id !== id);
    onTextOverlaysChange(updatedOverlays);
    if (activeOverlay === id) {
      setActiveOverlay(null);
    }
  };

  const processOfferText = (overlay) => {
    if (overlay.type !== 'offer' || !overlay.template) return overlay.text;
    
    let processedText = overlay.template.template;
    overlay.template.fields.forEach(field => {
      const value = overlay.fields[field] || `[${field}]`;
      processedText = processedText.replace(`{{${field}}}`, value);
    });
    return processedText;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-800">Text Overlays & Offers</h3>
        <div className="flex space-x-2">
          <button
            onClick={addTextOverlay}
            className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <SafeIcon icon={FiType} className="w-4 h-4" />
            <span className="text-sm">Add Text</span>
          </button>
        </div>
      </div>

      {/* Quick Offer Templates */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Quick Offer Templates</h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {offerTemplates.map((template) => (
            <button
              key={template.id}
              onClick={() => addOfferOverlay(template)}
              className="flex items-center space-x-2 p-2 bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors text-sm"
            >
              <SafeIcon icon={FiDollarSign} className="w-3 h-3 text-green-600" />
              <span className="truncate">{template.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Text Overlay List */}
      {textOverlays.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-700">Active Overlays</h4>
          {textOverlays.map((overlay) => (
            <div
              key={overlay.id}
              className={`border rounded-lg p-4 transition-all duration-200 ${
                activeOverlay === overlay.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <SafeIcon 
                    icon={overlay.type === 'offer' ? FiDollarSign : FiType} 
                    className="w-4 h-4 text-gray-500" 
                  />
                  <span className="text-sm font-medium text-gray-700">
                    {overlay.type === 'offer' ? 'Offer' : 'Text'} Overlay
                  </span>
                  {overlay.type === 'offer' && overlay.template && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                      {overlay.template.name}
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => updateOverlay(overlay.id, { visible: !overlay.visible })}
                    className={`p-1 rounded ${
                      overlay.visible 
                        ? 'text-blue-600 hover:bg-blue-100' 
                        : 'text-gray-400 hover:bg-gray-100'
                    }`}
                  >
                    <SafeIcon icon={overlay.visible ? FiEye : FiEyeOff} className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => removeOverlay(overlay.id)}
                    className="p-1 text-red-600 hover:bg-red-100 rounded"
                  >
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Overlay Configuration */}
              {activeOverlay === overlay.id && (
                <div className="space-y-4 border-t pt-4">
                  {/* Text Input */}
                  {overlay.type === 'custom' ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Text Content
                      </label>
                      <input
                        type="text"
                        value={overlay.text}
                        onChange={(e) => updateOverlay(overlay.id, { text: e.target.value })}
                        placeholder="Enter your text..."
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Offer Details
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {overlay.template.fields.map((field) => (
                          <div key={field}>
                            <input
                              type="text"
                              value={overlay.fields[field] || ''}
                              onChange={(e) => updateOverlay(overlay.id, {
                                fields: { ...overlay.fields, [field]: e.target.value }
                              })}
                              placeholder={`Enter ${field}...`}
                              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            />
                          </div>
                        ))}
                      </div>
                      <div className="mt-2 p-2 bg-gray-100 rounded text-sm text-gray-600">
                        Preview: {processOfferText(overlay)}
                      </div>
                    </div>
                  )}

                  {/* Position and Style Controls */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Position
                      </label>
                      <select
                        value={overlay.position}
                        onChange={(e) => updateOverlay(overlay.id, { position: e.target.value })}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {textPositionOptions.map((pos) => (
                          <option key={pos.id} value={pos.id}>
                            {pos.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Font Style
                      </label>
                      <select
                        value={overlay.fontStyle}
                        onChange={(e) => updateOverlay(overlay.id, { fontStyle: e.target.value })}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {fontStyleOptions.map((style) => (
                          <option key={style.id} value={style.id}>
                            {style.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Text Color
                      </label>
                      <input
                        type="color"
                        value={overlay.color}
                        onChange={(e) => updateOverlay(overlay.id, { color: e.target.value })}
                        className="w-full h-10 border border-gray-300 rounded-lg"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Font Size
                      </label>
                      <select
                        value={overlay.fontSize}
                        onChange={(e) => updateOverlay(overlay.id, { fontSize: e.target.value })}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="small">Small</option>
                        <option value="medium">Medium</option>
                        <option value="large">Large</option>
                        <option value="xl">Extra Large</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Background
                      </label>
                      <select
                        value={overlay.backgroundColor}
                        onChange={(e) => updateOverlay(overlay.id, { backgroundColor: e.target.value })}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="transparent">None</option>
                        <option value="rgba(0, 0, 0, 0.7)">Dark</option>
                        <option value="rgba(255, 255, 255, 0.9)">Light</option>
                        <option value="rgba(0, 123, 255, 0.9)">Blue</option>
                        <option value="rgba(220, 38, 38, 0.9)">Red</option>
                        <option value="rgba(16, 185, 129, 0.9)">Green</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Quick Preview */}
              {activeOverlay !== overlay.id && (
                <div 
                  className="mt-2 p-2 bg-gray-100 rounded text-sm cursor-pointer hover:bg-gray-200 transition-colors"
                  onClick={() => setActiveOverlay(overlay.id)}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">
                      {overlay.type === 'offer' ? processOfferText(overlay) : overlay.text || 'Click to edit...'}
                    </span>
                    <span className="text-xs text-gray-500 capitalize">
                      {overlay.position.replace('-', ' ')}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {textOverlays.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <SafeIcon icon={FiType} className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>No text overlays added yet.</p>
          <p className="text-sm">Click "Add Text" or choose a quick offer template to get started.</p>
        </div>
      )}
    </div>
  );
};

export default TextOverlayEditor;